/**
 * Thin client for the backend's dedicated Go routes.
 *
 * House rule: the portal never touches /api/collections/* — every capability
 * it needs is a named route in the Go backend. That is what keeps the
 * PocketBase API rules from becoming a second, invisible authorization
 * surface.
 *
 * Note the deliberate omission of the X-Installation-Id header the mobile app
 * sends: that header is what claims one of an account's five device slots
 * (moddevicelimit). Signing in on a laptop must never evict a kid's tablet.
 */

const TOKEN_KEY = 'rofoofee.portal.token';

// In a production build the portal is served from the backend's own origin, so
// /api resolves without configuration.
//
// `npm run dev` targets the host `pbship deploy -e dev` actually publishes to.
// Note this is NOT devapi101.rofoofee.com, which the manager dashboard still
// defaults to: that name resolves to the legacy dev box (91.98.38.187) and
// never receives pbship releases. Override either with VITE_API_BASE.
const API_BASE = (
  import.meta.env.VITE_API_BASE ||
  (import.meta.env.PROD ? window.location.origin : 'https://dev.rofoofee.65.109.232.63.sslip.io')
).replace(/\/+$/, '');

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

/** Error carrying the backend's machine-readable code, for i18n at the call site. */
export class ApiError extends Error {
  constructor(status, code, message) {
    super(message || code || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function request(path, { method = 'GET', body, auth = true, signal } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = token;
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
      // Authed reads must not come from the browser's HTTP cache: the backend
      // sends a short max-age on /api/user/userinfo, which would otherwise
      // serve a stale profile list right after an edit.
      cache: 'no-store',
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new ApiError(0, 'network_error', err?.message);
  }

  // 204 and empty bodies are legitimate answers from the mutation routes.
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    // PocketBase's apis.New*Error carries the code we passed in `message`, but
    // sentence-cases it and appends a period on the way out
    // ("invalid_or_expired" → "Invalid_or_expired."). Normalize it back so the
    // `errors.*` dictionary can be keyed on the code the Go handler wrote.
    const raw = data?.code || data?.message || `http_${res.status}`;
    const code = String(raw).trim().replace(/\.+$/, '').toLowerCase();
    throw new ApiError(res.status, code, data?.message);
  }

  return data;
}

// --- auth --------------------------------------------------------------

/** Exchange a Google *Web* ID token for an app auth token. */
export function authenticateWithGoogle(idToken) {
  return request('/api/auth/authenticate', {
    method: 'POST',
    auth: false,
    body: { id_token: idToken },
  });
}

/** Ask the backend to email a 6-digit sign-in code. Resolves { otp_id, expires_in }. */
export function requestOtp(email, lang) {
  return request('/api/auth/otp/request', {
    method: 'POST',
    auth: false,
    body: { email, lang },
  });
}

/**
 * Email + password sign-in. Only works for accounts created with a real
 * password in the app — Google/Apple accounts have a random one and must use
 * the social button or an emailed code.
 */
export function loginWithPassword(email, password) {
  return request('/api/auth/password', {
    method: 'POST',
    auth: false,
    body: { email, password },
  });
}

/** Exchange a code for an auth token. Resolves { token, record }. */
export function verifyOtp(otpId, code) {
  return request('/api/auth/otp/verify', {
    method: 'POST',
    auth: false,
    body: { otp_id: otpId, code },
  });
}

// Note: no deleteAccount() here on purpose. POST /api/auth/delete exists on the
// backend for the mobile app (store policy requires the flow to be reachable
// in-app), but the web portal must not expose an irreversible account wipe.

// --- account data ------------------------------------------------------

/** Resolves { user_record, profiles } for the signed-in parent. */
export function getUserInfo(signal) {
  return request('/api/user/userinfo', { signal });
}

/** The avatar pool kids can pick from. Resolves { items }. */
export function getAvatars(signal) {
  return request('/api/user/avatars', { auth: false, signal });
}

export function createProfile({ name, birthdate, avatarId }) {
  return request('/api/user/profile/create', {
    method: 'POST',
    body: { name, birthdate, avatar_id: avatarId },
  });
}

export function updateProfile({ id, name, birthdate, avatarId }) {
  const body = { id };
  if (name !== undefined) body.name = name;
  if (birthdate !== undefined) body.birthdate = birthdate;
  if (avatarId !== undefined) body.avatar_id = avatarId;
  return request('/api/user/profile/update', { method: 'POST', body });
}

export function deleteProfile(id) {
  return request('/api/user/profile/delete', { method: 'POST', body: { id } });
}

// --- subscriptions -----------------------------------------------------

/** Purchasable plans. Public — the pricing view works signed out. */
export function getPlans(signal) {
  return request('/api/payment/plans', { auth: false, signal });
}

/**
 * Current subscription plus where it came from. `source` is "tap" | "apple" |
 * "google" | "", and `can_cancel_here` is only true for a live web
 * subscription — a store one has to be cancelled in the store.
 */
export function getSubscription(signal) {
  return request('/api/payment/subscription', { signal });
}

/**
 * Start checkout. Sends only a plan slug — the price is resolved server-side,
 * so nothing here can influence what is charged. Resolves { payment_url }.
 */
export function subscribe(plan, { redirectUrl, lang } = {}) {
  return request('/api/payment/subscribe', {
    method: 'POST',
    body: { plan, redirect_url: redirectUrl, lang },
  });
}

/**
 * Ask Tap what became of a charge. Used after returning from checkout to tell
 * "declined" apart from "captured, webhook still in flight" — otherwise a
 * failed payment and a slow one look identical to the customer.
 */
export function checkCharge(chargeId, signal) {
  return request(`/api/payment/check-charge/${encodeURIComponent(chargeId)}`, {
    auth: false,
    signal,
  });
}

/** Stop auto-renew. Access continues until the end of the paid period. */
export function cancelSubscription() {
  return request('/api/payment/subscription/cancel', { method: 'POST', body: {} });
}

// --- files -------------------------------------------------------------

/** Build a URL for a PocketBase-stored file (avatars come back as collection/id/file). */
export function fileUrl(collectionId, recordId, filename, thumb = '') {
  if (!collectionId || !recordId || !filename) return null;
  const url = `${API_BASE}/api/files/${collectionId}/${recordId}/${filename}`;
  return thumb ? `${url}?thumb=${thumb}` : url;
}

export { API_BASE };
